import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import { authClient } from "@/lib/auth-client";
import {
  Users,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  ShieldCheck,
  List as ListIcon,
  LayoutGrid,
} from "lucide-react";
import { CertComplianceGrid } from "@/components/certifications/cert-compliance-grid";

export const Route = createFileRoute("/_authenticated/team-certifications")({
  component: TeamCertificationsPage,
});

interface TeamCert {
  id: string;
  userId: string;
  userName: string | null;
  certTypeName: string | null;
  customCertName: string | null;
  certNumber: string | null;
  expiryDate: string | null;
  status: string;
  reviewStatus: string;
}

interface TeamStats {
  total: number;
  valid: number;
  expiringSoon: number;
  expired: number;
  revoked: number;
  pendingReview: number;
}

const STATUS_CONFIG: Record<string, { color: string; icon: typeof CheckCircle; label: string }> = {
  valid: { color: "bg-green-100 text-green-800", icon: CheckCircle, label: "Valid" },
  expiring_soon: { color: "bg-yellow-100 text-yellow-800", icon: AlertTriangle, label: "Expiring" },
  expired: { color: "bg-red-100 text-red-800", icon: XCircle, label: "Expired" },
  revoked: { color: "bg-gray-100 text-gray-800", icon: XCircle, label: "Revoked" },
};

function TeamCertificationsPage() {
  const { data: session } = authClient.useSession();
  const role = (session?.user as any)?.role;

  // Only admin/div_admin/office_personnel can view team certifications
  if (role && !["admin", "div_admin", "office_personnel"].includes(role)) {
    return (
      <div className="max-w-lg mx-auto pt-8 text-center">
        <ShieldCheck className="h-12 w-12 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-lg font-medium text-muted-foreground">Access Denied</p>
        <p className="text-sm text-muted-foreground/70 mt-1">
          This page is available to foremen, managers, and admins.
        </p>
      </div>
    );
  }

  const { data, isLoading } = useQuery({
    queryKey: ["team-certifications"],
    queryFn: () =>
      apiGet<{ data: TeamCert[]; stats: TeamStats; teamMembers: number }>(
        "/api/certifications/team/summary"
      ),
  });

  const certs = data?.data || [];
  const stats = data?.stats;
  const teamMembers = data?.teamMembers || 0;

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>("all");
  // Phase 5: view toggle — default to existing list UX, opt-in grid.
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const filteredCerts = statusFilter === "all"
    ? certs
    : certs.filter((c) => c.status === statusFilter);

  // Group by user
  const byUser = new Map<string, { name: string; certs: TeamCert[] }>();
  for (const cert of filteredCerts) {
    const key = cert.userId;
    if (!byUser.has(key)) {
      byUser.set(key, { name: cert.userName || "Unknown", certs: [] });
    }
    byUser.get(key)!.certs.push(cert);
  }

  return (
    <div className={cn("space-y-4 mx-auto pb-24", viewMode === "list" ? "max-w-lg" : "max-w-3xl")}>
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">
          Team Certifications
        </h1>
        <div
          className="inline-flex rounded-lg border border-border bg-card p-0.5"
          role="tablist"
          aria-label="View mode"
        >
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "list"}
            onClick={() => setViewMode("list")}
            className={cn(
              "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              viewMode === "list"
                ? "bg-pph-charcoal text-white"
                : "text-muted-foreground"
            )}
          >
            <ListIcon className="h-3.5 w-3.5" />
            List
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={viewMode === "grid"}
            onClick={() => setViewMode("grid")}
            className={cn(
              "flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              viewMode === "grid"
                ? "bg-pph-charcoal text-white"
                : "text-muted-foreground"
            )}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            Grid
          </button>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && stats && (
        <>
          {/* Stats overview */}
          <div className="grid grid-cols-2 gap-2">
            <Card>
              <CardContent className="py-3 px-3 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.valid}</p>
                <p className="text-xs text-muted-foreground">Valid</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-3 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.expiringSoon}</p>
                <p className="text-xs text-muted-foreground">Expiring Soon</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-3 text-center">
                <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
                <p className="text-xs text-muted-foreground">Expired</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-3 px-3 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.pendingReview}</p>
                <p className="text-xs text-muted-foreground">Pending Review</p>
              </CardContent>
            </Card>
          </div>

          <p className="text-xs text-muted-foreground">
            <Users className="h-3 w-3 inline mr-1" />
            {teamMembers} team member{teamMembers !== 1 ? "s" : ""} &middot; {stats.total} total certifications
          </p>

          {/* Status filter */}
          <div className="flex gap-1.5 flex-wrap">
            {[
              { value: "all", label: "All" },
              { value: "valid", label: "Valid" },
              { value: "expiring_soon", label: "Expiring" },
              { value: "expired", label: "Expired" },
            ].map((f) => (
              <button
                key={f.value}
                className={cn(
                  "text-xs px-2.5 py-1 rounded-full border transition-colors",
                  statusFilter === f.value
                    ? "bg-pph-charcoal text-white border-pph-charcoal"
                    : "bg-background border-input text-muted-foreground hover:bg-accent"
                )}
                onClick={() => setStatusFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* View body: list (default) or compliance grid */}
          {viewMode === "grid" ? (
            <CertComplianceGrid certs={filteredCerts} />
          ) : filteredCerts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-sm text-muted-foreground">No certifications match this filter.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {Array.from(byUser.entries()).map(([userId, { name, certs: userCerts }]) => (
                <Card key={userId}>
                  <CardContent className="py-3 px-4">
                    <p className="text-sm font-semibold mb-2">{name}</p>
                    <div className="space-y-1.5">
                      {userCerts.map((cert) => {
                        const config = STATUS_CONFIG[cert.status] || STATUS_CONFIG.valid;
                        const StatusIcon = config.icon;
                        const certName = cert.certTypeName || cert.customCertName || "Unknown";
                        return (
                          <div key={cert.id} className="flex items-center justify-between text-xs">
                            <span className="truncate flex-1 text-muted-foreground">{certName}</span>
                            <div className="flex items-center gap-1.5 shrink-0 ml-2">
                              {cert.expiryDate && (
                                <span className="text-muted-foreground">
                                  {formatDateOnly(cert.expiryDate, "en-CA")}
                                </span>
                              )}
                              <Badge className={cn("text-[10px] px-1 py-0 gap-0.5", config.color)}>
                                <StatusIcon className="h-2.5 w-2.5" />
                                {config.label}
                              </Badge>
                              {cert.reviewStatus === "pending" && (
                                <Badge className="text-[10px] px-1 py-0 bg-blue-100 text-blue-800">
                                  <Clock className="h-2.5 w-2.5" />
                                </Badge>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

